// User types, groups and per-application access (docs/access.md, ADR 019).
//
// - A user type says *who* an account is (employee / customer / partner / custom): exactly one per user. It drives the
//   account lifecycle — self sign-up, email-domain mapping, 2FA rule, default groups.
// - Groups say *which teams* a user belongs to (many per user). Applications grant access to user types and groups.
// - Roles stay what they were: permissions inside the platform console / API v1.
import type { UserId } from "./types";

/** 2FA rule of a user type. `inherit` follows the global MFA policy (Sign-in method settings). */
export type UserTypeMfa = "inherit" | "optional" | "required";

export interface UserTypePolicy {
  /** Visitors may create an account of this type themselves (only through a client that signs up this type). */
  selfSignUp: boolean;
  /** Self sign-ups with these email domains get this type (`example.com`, `*.example.com`). */
  emailDomains: string[];
  mfa: UserTypeMfa;
  /** Groups a new account of this type joins automatically. */
  defaultGroups: string[];
}

export interface UserType {
  /** Immutable slug; appears in tokens as `user_type`. */
  id: string;
  name: string;
  description?: string;
  builtIn: boolean;
  policy: UserTypePolicy;
  createdAt: Date;
  updatedAt: Date;
}

/** Accounts without an explicit type (every account created before user types existed) are employees. */
export const DEFAULT_USER_TYPE = "employee";

export const BUILT_IN_USER_TYPES: ReadonlyArray<Pick<UserType, "id" | "name" | "description" | "policy">> = [
  {
    id: "employee",
    name: "employee",
    description: "Staff of the organization",
    policy: { selfSignUp: false, emailDomains: [], mfa: "inherit", defaultGroups: [] },
  },
  {
    id: "customer",
    name: "customer",
    description: "Customers using customer-facing applications",
    policy: { selfSignUp: true, emailDomains: [], mfa: "optional", defaultGroups: [] },
  },
  {
    id: "partner",
    name: "partner",
    description: "External partners with access to selected applications",
    policy: { selfSignUp: false, emailDomains: [], mfa: "inherit", defaultGroups: [] },
  },
];

export const BUILT_IN_USER_TYPE_IDS = BUILT_IN_USER_TYPES.map((t) => t.id);

export interface Group {
  /** Immutable slug; appears in tokens in the `groups` claim. */
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

export type ClientAccessMode = "all" | "restricted";

/** Who may sign in to an application (OAuth client). */
export interface ClientAccess {
  /** `all`: every account · `restricted`: only the listed user types and group members. */
  mode: ClientAccessMode;
  userTypes: string[];
  groups: string[];
  /** User type given to accounts that sign up through this application (absent = sign-up not offered). */
  signUpUserType?: string;
}

/** Clients registered before access control existed stay open. */
export const OPEN_CLIENT_ACCESS: ClientAccess = { mode: "all", userTypes: [], groups: [] };
/** New clients are closed by default: employees only. */
export const DEFAULT_NEW_CLIENT_ACCESS: ClientAccess = {
  mode: "restricted",
  userTypes: [DEFAULT_USER_TYPE],
  groups: [],
};

export interface AccessSubject {
  /** `null` = the root account (no type): no access rule admits it — it may only use the console client. */
  userType: string | null;
  groups: readonly string[];
}

/**
 * Whether an account may sign in to an application with the given access rules. The root account (`userType: null`)
 * is never admitted here, not even by `mode: "all"`; its console access is granted separately (`rootMayUseClient`).
 */
export function canAccessClient(access: ClientAccess | undefined, subject: AccessSubject): boolean {
  if (subject.userType === null) return false;
  if (!access || access.mode === "all") return true;
  return access.userTypes.includes(subject.userType) || subject.groups.some((g) => access.groups.includes(g));
}

/** `example.com` matches exactly; `*.example.com` matches sub-domains (not the apex). */
export function emailDomainMatches(email: string, pattern: string): boolean {
  const domain = email.slice(email.lastIndexOf("@") + 1).toLowerCase();
  const p = pattern.trim().toLowerCase();
  if (!p) return false;
  return p.startsWith("*.") ? domain.endsWith(p.slice(1)) : domain === p;
}

/**
 * User type of a self sign-up: an email-domain match wins (company domains → employee), otherwise the type the
 * application signs up — only when that type allows self sign-up. `null` = sign-up refused.
 */
export function resolveSignUpUserType(
  types: readonly Pick<UserType, "id" | "policy">[],
  email: string,
  clientSignUpType: string | undefined,
): string | null {
  const byDomain = types.find((t) => t.policy.emailDomains.some((d) => emailDomainMatches(email, d)));
  if (byDomain) return byDomain.id;
  if (!clientSignUpType) return null;
  const t = types.find((x) => x.id === clientSignUpType);
  return t?.policy.selfSignUp ? t.id : null;
}

/** Map of user id → type for a batch (missing ids = DEFAULT_USER_TYPE). */
export type UserTypeMap = ReadonlyMap<UserId, string>;

/** The root account signs in to the console and to no other application (docs/root-account.md). */
export function rootMayUseClient(clientId: string, consoleClientId: string): boolean {
  return clientId === consoleClientId;
}
